// The layout menu as plain data. Kept separate from the toolbar component so the rules that matter —
// which actions are available, which layout is marked, what the default toggle offers — are testable
// without mounting a DevExtreme widget. See [DevExtreme text is flaky in jsdom]: asserting a rendered
// dropdown's contents is unreliable, asserting the array it was built from is not.

export const GRID_LAYOUT_ACTIONS = Object.freeze({
  SaveAs: 'saveAs',
  Update: 'update',
  Rename: 'rename',
  SetDefault: 'setDefault',
  ClearDefault: 'clearDefault',
  Delete: 'delete',
  Export: 'export',
  Import: 'import',
  Reset: 'reset',
});

// The icons below are DevExtreme icon-font names. There is no dx-icon-star: the star glyph is
// 'favorites'. A name that does not exist fails silently as a blank space, so check any new one
// against devextreme/scss before using it.

// Layout names are user-entered and the server treats them case-insensitively, so the menu orders
// them the same way rather than pushing every lower-case name below every upper-case one.
function byNameIgnoringCase(left, right) {
  return String(left?.name ?? '').localeCompare(String(right?.name ?? ''), undefined, { sensitivity: 'accent' });
}

/**
 * Builds the dropdown items for one grid's layout menu.
 *
 * Layout entries come first, then the actions. Every action except Save as and Reset is scoped to
 * the **active** layout — the one currently applied — so the menu never has to ask which layout the
 * user meant. With nothing applied those actions are present but disabled, which keeps the menu the
 * same shape either way instead of items appearing and disappearing under the cursor.
 *
 * @param {{ layouts?: Array<{id: number, name: string, isDefault: boolean}>,
 *           activeLayout?: {id: number, name: string} | null }} [options]
 */
export function buildLayoutMenuItems({ layouts = [], activeLayout = null } = {}) {
  const ordered = [...layouts].sort(byNameIgnoringCase);

  const items = ordered.map((layout) => ({
    key: `layout:${layout.id}`,
    text: layout.name,
    layoutId: layout.id,
    isActive: activeLayout?.id === layout.id,
    isDefault: Boolean(layout.isDefault),
  }));

  if (items.length === 0) {
    items.push({ key: 'empty', text: 'No saved layouts', disabled: true });
  }

  const hasActive = Boolean(activeLayout);

  // Read the default flag off the list rather than off activeLayout, which only carries id and name.
  // A layout deleted or un-defaulted in another tab is simply not found, and not-found reads as
  // "not the default" — offering to set it again is harmless, offering to clear it would not be.
  const activeIsDefault = hasActive && Boolean(ordered.find((layout) => layout.id === activeLayout.id)?.isDefault);

  items.push({
    key: 'action:saveAs',
    action: GRID_LAYOUT_ACTIONS.SaveAs,
    text: 'Save as new layout...',
    icon: 'save',
    beginGroup: true,
  });

  items.push({
    key: 'action:update',
    action: GRID_LAYOUT_ACTIONS.Update,
    text: hasActive ? `Update "${activeLayout.name}"` : 'Update current layout',
    icon: 'edit',
    disabled: !hasActive,
  });

  items.push({
    key: 'action:rename',
    action: GRID_LAYOUT_ACTIONS.Rename,
    text: 'Rename...',
    icon: 'rename',
    disabled: !hasActive,
  });

  // Two mutually exclusive items rather than one item with a tick, so the menu states the action it
  // will take instead of the state it is in.
  items.push(
    activeIsDefault
      ? {
          key: 'action:clearDefault',
          action: GRID_LAYOUT_ACTIONS.ClearDefault,
          text: 'Remove as my default',
          icon: 'favorites',
        }
      : {
          key: 'action:setDefault',
          action: GRID_LAYOUT_ACTIONS.SetDefault,
          text: 'Set as my default',
          icon: 'favorites',
          disabled: !hasActive,
        }
  );

  items.push({
    key: 'action:delete',
    action: GRID_LAYOUT_ACTIONS.Delete,
    text: 'Delete...',
    icon: 'trash',
    disabled: !hasActive,
  });

  // Their own group: these two move a layout between people rather than managing this user's own.
  // There are no shared layouts, so a file is how a layout reaches a colleague.
  //
  // Export is scoped to the active layout like the actions above it — it writes what was saved, not
  // whatever the grid is showing at that moment. Import is not scoped to anything: being sent a file
  // for a grid you have saved nothing on is the ordinary case.
  items.push({
    key: 'action:export',
    action: GRID_LAYOUT_ACTIONS.Export,
    text: hasActive ? `Export "${activeLayout.name}"...` : 'Export layout...',
    icon: 'export',
    disabled: !hasActive,
    beginGroup: true,
  });

  items.push({
    key: 'action:import',
    action: GRID_LAYOUT_ACTIONS.Import,
    text: 'Import layout...',
    icon: 'import',
  });

  // Its own group: this one discards the current view rather than managing a saved layout.
  items.push({
    key: 'action:reset',
    action: GRID_LAYOUT_ACTIONS.Reset,
    text: 'Reset to default view',
    icon: 'revert',
    beginGroup: true,
  });

  return items;
}

/** The toolbar button's label: the applied layout's name, so the grid says what it is showing. */
export function getLayoutButtonText(activeLayout) {
  return activeLayout?.name ?? 'Layout';
}
